import { useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { type Account, type QueryAccountsArgs } from '@pkg/graphql/codegen/graphql';
import { useAppSelector } from '@pkg/hooks';

import { NFT_SECTION_ITEM_LIMIT } from '@app/pages/ProfilePage/constants';
import { useAuthTokens } from '@app/store/zustand/auth/useAuthTokens';

import { callGetConnectedWalletsByUser } from './request';

export const USER_WALLETS_BY_USER_CACHE = 'connected-wallets-by-user-query';

interface FetchWalletsArgs {
    disabled?: boolean;
    useCurrentUser?: boolean;
    variables?: QueryAccountsArgs;
}

// Limit can be between 50 and 500
export function useWalletsQueryById(args?: FetchWalletsArgs) {
    const { disabled = false, useCurrentUser = false, variables } = args || {};
    const pageUserId = useAppSelector((s) => s.yatPage.user_id);
    const currentUserId = useAuthTokens.getState().currentUserId;

    const pageOwnerIsCurrentUser = useMemo(
        () => Boolean(useCurrentUser || (pageUserId && pageUserId === currentUserId)),
        [currentUserId, pageUserId, useCurrentUser],
    );
    // TODO: dedup from orders/make helper to check
    const userFilter = pageOwnerIsCurrentUser ? { current: true } : { id: pageUserId };
    const missingPageId = !pageOwnerIsCurrentUser ? !pageUserId?.length : false;
    const enabled = !disabled && !missingPageId;
    return useInfiniteQuery({
        queryKey: [USER_WALLETS_BY_USER_CACHE, pageUserId ?? currentUserId],
        queryFn: ({ signal, pageParam }) =>
            callGetConnectedWalletsByUser({
                variables: {
                    userFilter,
                    cursorInput: {
                        limit: variables?.cursorInput?.limit || NFT_SECTION_ITEM_LIMIT,
                        cursor: pageParam || null,
                    },
                },
                settings: { signal },
            }),
        retry: false,
        enabled,
        staleTime: Infinity,
        initialPageParam: '',
        getNextPageParam: (data) => {
            if (data) {
                return data?.accounts?.cursor || null;
            }
        },
        select: (data) => {
            const pages = data.pages.reduce<Array<Account>>((accumulator, current) => {
                if (current && current?.accounts?.accounts) {
                    return [...accumulator, current?.accounts?.accounts].flat();
                }
                return accumulator.filter((page) => !!page);
            }, []);
            return {
                pages: pages ?? [],
                pageParams: data.pageParams,
            };
        },
    });
}
